import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssignmentGetAutoApproveQualityFlags } from 'api/assignment/assignment';
import { AutoApproveQualityFlagType } from 'api/model/autoApproveQualityFlagType';
import type { QualityFlagDetail } from 'api/model/qualityFlagDetail';
import { useMemo, useState } from 'react';
import { generatePath, Link } from 'react-router-dom';
import Routes from 'constants/Routes';
import styled from 'styled-components';
import themes from 'constants/Colors';
import themeSizes from 'constants/Sizes';
import DateRangeFilter, {
  DateRange,
  endOfDay,
  FilterSelect,
  presetForDays,
  startOfDay,
} from 'pages/Quality/DateRangeFilter';

const PageContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PageTitle = styled.h1`
  font-size: ${themeSizes.font.large};
  margin-bottom: 10px;
`;

const Card = styled.div`
  padding: 15px;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h2`
  font-size: ${themeSizes.font.medium};
  margin-bottom: 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${themes.light.border.primary};
`;

const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
`;

const Tag = styled.span<{ color?: string }>`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  background-color: ${({ color }) => (color ? `${color}20` : '#eee')};
  color: ${({ color }) => color ?? '#333'};
  margin: 1px 2px;
  white-space: nowrap;
`;

const COLORS = {
  green: '#28a745',
  red: '#dc3545',
  blue: '#17a2b8',
  orange: '#fd7e14',
  purple: '#6f42c1',
  teal: '#20c997',
  gray: '#6c757d',
};

const StatBox = ({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
}) => (
  <div
    style={{
      textAlign: 'center',
      padding: '15px',
      backgroundColor: `${color}15`,
      borderRadius: '8px',
      border: `2px solid ${color}`,
    }}
  >
    <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    <div style={{ fontSize: '14px', fontWeight: '500', marginTop: '5px' }}>
      {label}
    </div>
    {sub && (
      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
        {sub}
      </div>
    )}
  </div>
);

// Etiketter för kända flaggtyper. Nya enumvärden (framtida regler) renderas via
// fallbacken nedan utan att den här sidan behöver ändras.
const FLAG_TYPE_LABELS: Record<number, string> = {
  [AutoApproveQualityFlagType.StartPositionMissing]: 'Startposition saknas',
  [AutoApproveQualityFlagType.EndPositionMissing]: 'Slutposition saknas',
  [AutoApproveQualityFlagType.GpsRejected]: 'GPS avslagen',
  [AutoApproveQualityFlagType.EndPositionOutsideRadius]:
    'Slutposition utanför radie',
  [AutoApproveQualityFlagType.StartPositionOutsideRadius]:
    'Startposition utanför radie',
  [AutoApproveQualityFlagType.FuelOverrun]: 'Övertankning',
  [AutoApproveQualityFlagType.LocalTransitTicketInHomeCounty]:
    'Lokaltrafikbiljett i hemlän',
};

const flagTypeLabel = (flagType: number): string => {
  if (FLAG_TYPE_LABELS[flagType]) return FLAG_TYPE_LABELS[flagType];
  const enumName = Object.entries(AutoApproveQualityFlagType).find(
    ([, value]) => value === flagType
  )?.[0];
  return enumName ?? `Flaggtyp ${flagType}`;
};

const FLAG_TYPE_COLORS: Record<number, string> = {
  [AutoApproveQualityFlagType.StartPositionMissing]: COLORS.orange,
  [AutoApproveQualityFlagType.EndPositionMissing]: COLORS.orange,
  [AutoApproveQualityFlagType.GpsRejected]: COLORS.red,
  [AutoApproveQualityFlagType.EndPositionOutsideRadius]: COLORS.orange,
  [AutoApproveQualityFlagType.StartPositionOutsideRadius]: COLORS.orange,
  [AutoApproveQualityFlagType.FuelOverrun]: COLORS.purple,
  [AutoApproveQualityFlagType.LocalTransitTicketInHomeCounty]: COLORS.teal,
};

const flagTypeColor = (flagType: number): string =>
  FLAG_TYPE_COLORS[flagType] ?? COLORS.gray;

const STATUS_LABELS: Record<string, string> = {
  Created: 'Skapad',
  Complete: 'Slutförd',
  Approved: 'Godkänd',
  Canceled: 'Avbokad',
  Aborted: 'Avbruten',
  Discarded: 'Borttagen',
};

type ApprovalKind = 'auto' | 'manual' | 'notApproved';

const approvalKind = (d: QualityFlagDetail): ApprovalKind => {
  if (d.status !== 'Approved') return 'notApproved';
  return d.autoApprovedAt ? 'auto' : 'manual';
};

const APPROVAL_LABELS: Record<ApprovalKind, { label: string; color: string }> =
  {
    auto: { label: 'Autogodkänd', color: COLORS.green },
    manual: { label: 'Manuellt godkänd', color: COLORS.blue },
    notApproved: { label: 'Ej godkänd', color: COLORS.gray },
  };

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px',
  borderBottom: '2px solid #ddd',
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = { padding: '6px 8px', fontSize: '13px' };

type ApprovalFilter = 'approved' | 'notApproved' | 'all';

const QualityFlagsPage = () => {
  const [range, setRange] = useState<DateRange>(() => presetForDays(30));
  // Default: godkända uppdrag — vyn handlar om "godkänt med anmärkning".
  const [approvalFilter, setApprovalFilter] =
    useState<ApprovalFilter>('approved');
  const [flagTypeFilter, setFlagTypeFilter] = useState<number | 'all'>('all');

  const queryParams = useMemo(
    () => ({
      from: startOfDay(range.fromYmd),
      to: endOfDay(range.toYmd),
    }),
    [range]
  );

  const { data, isLoading } =
    useAssignmentGetAutoApproveQualityFlags(queryParams);

  if (isLoading || !data) {
    return (
      <PageContainer>
        <PageTitle>Godkänt med anmärkning</PageTitle>
        <DateRangeFilter range={range} onChange={setRange} />
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p style={{ marginTop: '10px' }}>Laddar kvalitetsflaggor...</p>
        </div>
      </PageContainer>
    );
  }

  const { summary, flags } = data;

  const totalFlags = flags.length;
  const flagsOnApproved = flags.filter(
    (f) => approvalKind(f) !== 'notApproved'
  ).length;
  const uniqueAssignments = new Set(flags.map((f) => f.assignmentId)).size;

  const visibleFlags = flags.filter((f) => {
    if (flagTypeFilter !== 'all' && f.flagType !== flagTypeFilter) return false;
    const kind = approvalKind(f);
    if (approvalFilter === 'approved') return kind !== 'notApproved';
    if (approvalFilter === 'notApproved') return kind === 'notApproved';
    return true;
  });

  return (
    <PageContainer>
      <PageTitle>Godkänt med anmärkning</PageTitle>
      <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
        Icke-blockerande kvalitetsflaggor från autogodkännandets utvärdering.
        Flaggorna stoppar inte godkännandet utan sparas för
        kvalitetsuppföljning.
      </p>
      <DateRangeFilter range={range} onChange={setRange} />

      <KPIGrid>
        <StatBox
          label="Flaggor i perioden"
          value={String(totalFlags)}
          color={COLORS.blue}
        />
        <StatBox
          label="På godkända uppdrag"
          value={String(flagsOnApproved)}
          sub="godkänt med anmärkning"
          color={COLORS.orange}
        />
        <StatBox
          label="Unika uppdrag"
          value={String(uniqueAssignments)}
          color={COLORS.gray}
        />
      </KPIGrid>

      <Card>
        <CardTitle>Per flaggtyp</CardTitle>
        {summary.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Inga kvalitetsflaggor i valt fönster.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Flaggtyp', 'Antal', 'Varav på godkända uppdrag'].map((h) => (
                  <th key={h} style={thStyle}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.flagType}>
                  <td style={tdStyle}>
                    <Tag color={flagTypeColor(row.flagType)}>
                      {flagTypeLabel(row.flagType)}
                    </Tag>
                  </td>
                  <td style={tdStyle}>{row.count}</td>
                  <td style={tdStyle}>{row.approvedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <CardTitle>
          Flaggade uppdrag ({visibleFlags.length} av {totalFlags})
        </CardTitle>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <span style={{ fontSize: '13px', color: '#666' }}>Visa:</span>
          <FilterSelect
            value={approvalFilter}
            onChange={(e) =>
              setApprovalFilter(e.target.value as ApprovalFilter)
            }
          >
            <option value="approved">Godkända uppdrag</option>
            <option value="notApproved">Ej godkända uppdrag</option>
            <option value="all">Alla</option>
          </FilterSelect>
          <span style={{ fontSize: '13px', color: '#666' }}>Flaggtyp:</span>
          <FilterSelect
            value={String(flagTypeFilter)}
            onChange={(e) =>
              setFlagTypeFilter(
                e.target.value === 'all' ? 'all' : Number(e.target.value)
              )
            }
          >
            <option value="all">Alla</option>
            {summary.map((row) => (
              <option key={row.flagType} value={row.flagType}>
                {flagTypeLabel(row.flagType)}
              </option>
            ))}
          </FilterSelect>
        </div>
        {visibleFlags.length === 0 ? (
          <p style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
            Inga kvalitetsflaggor matchar filtren i valt fönster.
          </p>
        ) : (
          <div style={{ overflowY: 'auto', maxHeight: '600px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    'Uppdrag',
                    'Flaggad',
                    'Flaggtyp',
                    'Meddelande',
                    'Fälttestare',
                    'Status',
                    'Godkännande',
                  ].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleFlags.map((f, i) => {
                  const approval = APPROVAL_LABELS[approvalKind(f)];
                  return (
                    <tr key={`${f.assignmentId}-${f.flagType}-${i}`}>
                      <td style={tdStyle}>
                        <Link
                          // Kvalitetsroutens egen ärendemodal — inte
                          // Efterkontrolls, vars rollspärr annars kastar ut
                          // admins som bara har kvalitetsrollen.
                          to={generatePath(Routes.quality.case, {
                            id: String(f.caseId),
                          })}
                          style={{
                            color: COLORS.blue,
                            textDecoration: 'none',
                          }}
                        >
                          {f.assignmentId}
                        </Link>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {f.created.toLocaleString('sv-SE')}
                      </td>
                      <td style={tdStyle}>
                        <Tag color={flagTypeColor(f.flagType)}>
                          {flagTypeLabel(f.flagType)}
                        </Tag>
                      </td>
                      <td style={{ ...tdStyle, fontSize: '12px' }}>
                        {f.message}
                      </td>
                      <td style={tdStyle}>{f.fieldTesterName ?? '-'}</td>
                      <td style={tdStyle}>
                        {STATUS_LABELS[f.status] ?? f.status}
                      </td>
                      <td style={tdStyle}>
                        <Tag color={approval.color}>{approval.label}</Tag>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
};

export default QualityFlagsPage;
